
export function cleanConditionSyntax(condStr, conditionData = null) {
    if (!condStr && !conditionData) return '';
    let str = (condStr || '').trim();

    // If condStr is missing or corrupted with raw HTML / broken tag, try reconstructing from conditionData first
    if (conditionData && (!str || str.includes('<span') || str.includes('data-variable') || str.includes('data-label') || str === '[' || str === '[]')) {
        try {
            const parsed = typeof conditionData === 'string' ? JSON.parse(conditionData) : conditionData;
            const rules = parsed?.rules || parsed?.conditions || [];
            if (Array.isArray(rules) && rules.length > 0) {
                const parts = rules.map(r => {
                    let field = (r.variable || r.field || '').trim();
                    field = field.replace(/^<<\s*/, '').replace(/\s*>>$/, '');
                    field = field.replace(/<\/?(span|strong|b|em|i|u|p|div|mark)\b[^>]*>/gi, '').trim();
                    const op = (r.operator || 'exists').toLowerCase();
                    let val = r.value !== undefined && r.value !== null ? String(r.value).trim() : '';
                    val = val.replace(/<\/?(span|strong|b|em|i|u|p|div|mark)\b[^>]*>/gi, '').trim();
                    if (op === 'exists' || op === 'is_not_empty') return '<<' + field + '>>';
                    if (op === 'not_exists' || op === 'is_empty') return '<<' + field + ">> == ''";
                    if (op === 'equals' || op === '==') return val ? '<<' + field + '>> == ' + val : '<<' + field + '>>';
                    if (op === 'not_equals' || op === '!=') return '<<' + field + '>> != ' + val;
                    if (op === 'greater_than' || op === '>') return '<<' + field + '>> > ' + val;
                    if (op === 'less_than' || op === '<') return '<<' + field + '>> < ' + val;
                    if (op === 'greater_than_or_equal' || op === '>=') return '<<' + field + '>> >= ' + val;
                    if (op === 'less_than_or_equal' || op === '<=') return '<<' + field + '>> <= ' + val;
                    return val ? '<<' + field + '>> ' + op + ' ' + val : '<<' + field + '>>';
                });
                const joiner = (parsed.operator === '||' || parsed.operator === 'OR') ? ' || ' : ' && ';
                const reconstructed = parts.join(joiner);
                if (reconstructed) return reconstructed;
            }
            if (parsed?.label) {
                const cleanLbl = cleanConditionSyntax(parsed.label);
                if (cleanLbl) return cleanLbl;
            }
        } catch (e) {}
    }

    // 1. Convert span tags with data-label or data-variable-key into <<Variable>>
    str = str.replace(/<span[^>]*data-label=["']([^"']+)["'][^>]*>(?:<\/span>)?/gi, '<<$1>>');
    str = str.replace(/<span[^>]*data-variable-key=["']([^"']+)["'][^>]*>(?:<\/span>)?/gi, '<<$1>>');

    // Also handle partially closed spans: <span ... data-label="Foo" ... (no closing >)
    str = str.replace(/<span[^>]*data-label=["']([^"']+)["']?[^>]*$/gi, '<<$1>>');
    str = str.replace(/<span[^>]*data-variable-key=["']([^"']+)["']?[^>]*$/gi, '<<$1>>');

    // 2. Remove any remaining broken / unclosed / stray <span... tags (with or without brackets)
    str = str.replace(/<span\b[^>]*?(?:\/?>|$)/gi, '');
    str = str.replace(/<\/span>/gi, '');

    // 3. Protect << and >> before stripping general HTML tags
    str = str
        .replace(/<</g, '\uE000')
        .replace(/>>/g, '\uE001')
        .replace(/&lt;&lt;/g, '\uE000')
        .replace(/&gt;&gt;/g, '\uE001')
        .replace(/«/g, '\uE000')
        .replace(/»/g, '\uE001')
        .replace(/&#60;&#60;/g, '\uE000')
        .replace(/&#62;&#62;/g, '\uE001');

    // 4. Strip any remaining HTML tags safely without touching protected << and >>
    str = str.replace(/<\/?(span|strong|b|em|i|u|s|strike|sub|sup|br|p|div|font|a|mark)\b[^>]*>/gi, '');
    str = str.replace(/<[^>\uE000\uE001]+>/g, '');

    // 5. Clean stray broken tag fragments like 'data-variable-tag' or 'data-variable-key='
    str = str.replace(/data-variable-[a-z0-9_-]+(=["']?[^"'\s\]]*["']?)?/gi, '');
    str = str.replace(/data-[a-z0-9_-]+(=["']?[^"'\s\]]*["']?)?/gi, '');

    // 6. Restore << and >>
    str = str.replace(/\uE000/g, '<<').replace(/\uE001/g, '>>');

    // 7. Clean surrounding brackets if any
    str = str.replace(/^\[\s*/, '').replace(/\s*\]$/, '').trim();

    // 8. Decode HTML entities
    str = str
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&#60;/g, '<')
        .replace(/&#62;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&');

    return str.trim();
}

// Precompiled Regexes for high-performance template parsing
const SEP = '(?:\\s|&nbsp;|\\u00a0|<\\/?[a-z0-9]+[^>]*>)*';
const INDEX_PAT = '\\{(\\s*(?:\\(?\\d+[\\.\\)]|\\(?[a-zA-Z][\\.\\)]|\\(?[ivxlcdmIVXLCDM]+[\\.\\)])\\s*)\\}';
const THREE_PART_REGEX = new RegExp(INDEX_PAT + SEP + '\\{([^{}]+)\\}' + SEP + '\\{([^{}]+)\\}', 'gi');
const UNBRACKETED_REGEX = new RegExp(INDEX_PAT + SEP + '(\\*\\*[\\s\\S]*?\\*\\*|Mr\.\/Ms\.|Mr\.|Ms\.|Shri|Smt|M\/s|Dr\.|Prof\.|Messrs)' + SEP + '\\{([^{}]+)\\}', 'gi');
const TWO_PART_REGEX = new RegExp(INDEX_PAT + SEP + '\\{([^{}]+)\\}', 'gi');

const STRIP_SPAN_REGEX = /<span[^>]*>([\s\S]*?)<\/span>/gi;
const NORM_BRACE_STRONG_REGEX = /<strong>\s*\{([\s\S]*?)\}\s*<\/strong>/gi;
const NORM_BRACE_B_REGEX = /<b>\s*\{([\s\S]*?)\}\s*<\/b>/gi;
const NORM_BRACE_STRONG_SPLIT_REGEX = /<strong>\s*\{\s*<\/strong>([\s\S]*?)<strong>\s*\}\s*<\/strong>/gi;
const NORM_BRACE_B_SPLIT_REGEX = /<b>\s*\{\s*<\/b>([\s\S]*?)<b>\s*\}\s*<\/b>/gi;
const NORM_BRACE_STAR_REGEX = /\*\*\s*\{([\s\S]*?)\}\s*\*\*/gi;

export function normalizeAndMergeFirstLines(htmlContent) {
    if (!htmlContent || !htmlContent.includes('{') || !htmlContent.includes('}')) return htmlContent || '';

    // 1. Strip span wrappers around braces if any: <span>{ 1. }</span> -> { 1. }
    htmlContent = htmlContent.replace(STRIP_SPAN_REGEX, '$1');

    // 2. Normalize brace tags:
    htmlContent = htmlContent.replace(NORM_BRACE_STRONG_REGEX, ' {$1} ');
    htmlContent = htmlContent.replace(NORM_BRACE_B_REGEX, ' {$1} ');
    htmlContent = htmlContent.replace(NORM_BRACE_STRONG_SPLIT_REGEX, ' {$1} ');
    htmlContent = htmlContent.replace(NORM_BRACE_B_SPLIT_REGEX, ' {$1} ');
    htmlContent = htmlContent.replace(NORM_BRACE_STAR_REGEX, ' {$1} ');

    // 3-part pattern: { 1. } { Mr./Ms. } { << Borrower Name >> } -> { 1. Mr./Ms. << Borrower Name >> }
    THREE_PART_REGEX.lastIndex = 0;
    htmlContent = htmlContent.replace(THREE_PART_REGEX, (match, p1, p2, p3) => {
        if (!p1.includes('[') && !p2.includes('[') && !p3.includes('[')) {
            return '{' + p1.trim() + ' ' + p2.trim() + ' ' + p3.trim() + '}';
        }
        return match;
    });

    // Unbracketed salutation in the middle: { 4. } Mr./Ms. { << Co-Guarantor Name >> }
    UNBRACKETED_REGEX.lastIndex = 0;
    htmlContent = htmlContent.replace(UNBRACKETED_REGEX, (match, p1, p2, p3) => {
        if (!p1.includes('[') && !p3.includes('[')) {
            return '{' + p1.trim() + ' ' + p2.trim() + ' ' + p3.trim() + '}';
        }
        return match;
    });

    // 2-part pattern: { 1. } { Mr./Ms. << Borrower Name >> } or { 1. } { << Borrower Name >> }
    TWO_PART_REGEX.lastIndex = 0;
    htmlContent = htmlContent.replace(TWO_PART_REGEX, (match, p1, p2) => {
        if (!p1.includes('[') && !p2.includes('[')) {
            return '{' + p1.trim() + ' ' + p2.trim() + '}';
        }
        return match;
    });

    return htmlContent;
}

export function isConditionBracket(bracketText) {
    if (!bracketText) return false;
    let text = (bracketText || '')
        .replace(/<\/?(span|strong|b|em|i|u|s|strike|sub|sup|br|p|div|font|a|mark)\b[^>]*>/gi, '')
        .replace(/&lt;&lt;/g, '<<')
        .replace(/&gt;&gt;/g, '>>')
        .replace(/&laquo;/g, '«')
        .replace(/&raquo;/g, '»')
        .replace(/&amp;/g, '&')
        .replace(/&gt;/g, '>')
        .replace(/&lt;/g, '<')
        .replace(/&nbsp;/g, ' ')
        .replace(/\u00a0/g, ' ')
        .replace(/^\s*\[\s*/, '')
        .replace(/\s*\]\s*$/, '')
        .replace(/\*\*/g, '')
        .replace(/__/g, '')
        .trim();

    return text.length > 0;
}

export function parseConditionString(condStr) {
    let text = (condStr || '')
        .replace(/<\/?(span|strong|b|em|i|u|s|strike|sub|sup|br|p|div|font|a|mark)\b[^>]*>/gi, '')
        .replace(/&lt;&lt;/g, '<<')
        .replace(/&gt;&gt;/g, '>>')
        .replace(/&laquo;/g, '«')
        .replace(/&raquo;/g, '»')
        .replace(/&amp;/g, '&')
        .replace(/&gt;/g, '>')
        .replace(/&lt;/g, '<')
        .replace(/&nbsp;/g, ' ')
        .replace(/\u00a0/g, ' ')
        .replace(/^\s*\[\s*/, '')
        .replace(/\s*\]\s*$/, '')
        .replace(/\*\*/g, '')
        .replace(/__/g, '')
        .trim();

    text = text
        .replace(/<</g, '\uE000')
        .replace(/>>/g, '\uE001')
        .replace(/«/g, '\uE000')
        .replace(/»/g, '\uE001')
        .replace(/\uE000\s*([^<>«»\uE000\uE001]+)\s*>/g, '\uE000$1\uE001')
        .replace(/<\s*([^<>«»\uE000\uE001]+)\s*\uE001/g, '\uE000$1\uE001')
        .replace(/<\s*([^<>«»\uE000\uE001=!<]+)\s*>/g, '\uE000$1\uE001')
        .replace(/\uE000/g, '<<')
        .replace(/\uE001/g, '>>');

    const varTokens = [];
    text = text.replace(/(?:<<|«)([\s\S]*?)(?:>>|»)/g, (match, vName) => {
        const idx = varTokens.length;
        varTokens.push(vName.trim());
        return `__VARTOKEN_${idx}__`;
    });

    let clean = text
        .replace(/^condition\s*:\s*/i, '')
        .replace(/^if\s+/i, '')
        .replace(/^\[\s*/, '')
        .replace(/\s*\]$/, '')
        .trim();

    const isOr = /\bOR\b|\|\|/i.test(clean);
    const rawParts = clean.split(/\s+(?:AND|OR|&&|\|\|)\s+/i);

    const rules = rawParts.map(part => {
        let p = part.trim();
        let op = 'exists';
        let val = '';
        let varName = '';

        if (p.includes('__VARTOKEN_')) {
            const varMatch = p.match(/__VARTOKEN_(\d+)__/);
            if (varMatch) {
                varName = varTokens[Number(varMatch[1])] || '';
                const rest = p.replace(varMatch[0], '').trim();
                if (rest) {
                    if (/(?:===|==|=)/.test(rest)) {
                        op = 'equals';
                        val = rest.replace(/^[!=<>=~]+\s*/, '').trim();
                    } else if (/(?:!==|!=)/.test(rest)) {
                        op = 'not_equals';
                        val = rest.replace(/^[!=<>=~]+\s*/, '').trim();
                    } else if (/>=/.test(rest)) {
                        op = 'greater_than_or_equal';
                        val = rest.replace(/^>=\s*/, '').trim();
                    } else if (/<=/.test(rest)) {
                        op = 'less_than_or_equal';
                        val = rest.replace(/^<=\s*/, '').trim();
                    } else if (/>/.test(rest)) {
                        op = 'greater_than';
                        val = rest.replace(/^>\s*/, '').trim();
                    } else if (/<(?![<])/.test(rest)) {
                        op = 'less_than';
                        val = rest.replace(/^<\s*/, '').trim();
                    } else if (/\bdoes\s+not\s+exist\b|\bis_empty\b|\bnot\s+exists\b/i.test(rest)) {
                        op = 'not_exists';
                        val = '';
                    } else if (/\bexists\b|\bis_not_empty\b/i.test(rest)) {
                        op = 'exists';
                        val = '';
                    } else if (/\bcontains\b/i.test(rest)) {
                        op = 'contains';
                        val = rest.replace(/^\bcontains\b\s*/i, '').trim();
                    } else if (/\bnot\s+contains\b/i.test(rest)) {
                        op = 'does_not_contain';
                        val = rest.replace(/^\bnot\s+contains\b\s*/i, '').trim();
                    } else {
                        val = rest;
                        op = 'equals';
                    }
                } else {
                    op = 'exists';
                    val = '';
                }
            }
        } else {
            if (/(?:===|==)/.test(p)) {
                const s = p.split(/===|==/);
                varName = s[0].trim();
                op = 'equals';
                val = s.slice(1).join('==').trim();
            } else if (/(?<![!=<>])=(?![=><])/.test(p)) {
                const s = p.split('=');
                varName = s[0].trim();
                op = 'equals';
                val = s.slice(1).join('=').trim();
            } else if (/!==|!=/.test(p)) {
                const s = p.split(/!==|!=/);
                varName = s[0].trim();
                op = 'not_equals';
                val = s.slice(1).join('!=').trim();
            } else if (/>=/.test(p)) {
                const s = p.split(/>=/);
                varName = s[0].trim();
                op = 'greater_than_or_equal';
                val = s.slice(1).join('>=').trim();
            } else if (/<=/.test(p)) {
                const s = p.split(/<=/);
                varName = s[0].trim();
                op = 'less_than_or_equal';
                val = s.slice(1).join('<=').trim();
            } else if (/>/.test(p)) {
                const s = p.split(/>/);
                varName = s[0].trim();
                op = 'greater_than';
                val = s.slice(1).join('>').trim();
            } else if (/<(?![<])/.test(p)) {
                const s = p.split(/</);
                varName = s[0].trim();
                op = 'less_than';
                val = s.slice(1).join('<').trim();
            } else if (/\bdoes\s+not\s+exist\b|\bis_empty\b|\bnot\s+exists\b/i.test(p)) {
                varName = p.replace(/\bdoes\s+not\s+exist\b|\bis_empty\b|\bnot\s+exists\b/i, '').trim();
                op = 'not_exists';
                val = '';
            } else if (/\bexists\b|\bis_not_empty\b/i.test(p)) {
                varName = p.replace(/\bexists\b|\bis_not_empty\b/i, '').trim();
                op = 'exists';
                val = '';
            } else {
                varName = p.trim();
                op = 'exists';
                val = '';
            }
        }

        const cleanVar = varName.replace(/(?:<<|>>|«|»)/g, '').replace(/^['"]|['"]$/g, '').trim();
        const cleanVal = val.replace(/^['"]|['"]$/g, '').trim();

        return {
            field: cleanVar,
            variable: cleanVar,
            operator: op,
            value: cleanVal
        };
    }).filter(r => r.field);

    return {
        operator: isOr ? '||' : '&&',
        rules
    };
}

export function normalizeSplitSignatures(htmlContent) {
    if (!htmlContent) return '';

    // Matches: {= [cond]} followed by (optional paragraph/div/span tags or spaces) alignment ! w h filename
    // e.g. <p>{= [<<POA NAME>> == J.Santhosh Kumar]}</p>\s*<p>== ! 0.9 0.5 J_Santhosh_KumarSign.png</p>
    // or {= [<<POA NAME>> == J.Santhosh Kumar]} == ! 0.9 0.5 J_Santhosh_KumarSign.png
    const splitSigRegex = /\{\s*(?:=&gt;|=>|&#61;&gt;|==|&lt;=|<=|=)?\s*(?:<[a-zA-Z0-9:-]+[^>]*>)*\[\s*([^[\\\]]+?)\s*\](?:<\/[a-zA-Z0-9:-]+>)*\s*\}\s*(?:<\/(?:p|div|span)>\s*<(?:p|div|span)[^>]*>|\s+)\s*(?:(=&gt;|=>|&#61;&gt;|==|&lt;=|<=|&gt;|>|&lt;|<|=)\s*)?!\s*([^\s<]+)\s+([^\s<]+)\s+([a-zA-Z0-9_.-]+\.[a-zA-Z0-9]+|[a-zA-Z0-9_.-]*WhiteBlankImage[a-zA-Z0-9_.-]*|[a-zA-Z0-9_.-]+(?=\s|$|<|\/))/gi;

    htmlContent = htmlContent.replace(splitSigRegex, (match, condStr, alignRaw, w, h, fn) => {
        let align = '=>';
        if (alignRaw) {
            const a = alignRaw.replace(/&gt;/g, '>').replace(/&lt;/g, '<').trim();
            if (a === '==') align = '==';
            else if (a === '<=' || a === '<') align = '<=';
            else if (a === '=>' || a === '>' || a === '=') align = '=>';
        }
        const cleanCond = cleanConditionSyntax(condStr);
        return `{${align} ! ${w} ${h} ${fn} [${cleanCond}] }`;
    });

    return htmlContent;
}

export function processInlineSyntax(htmlContent) {
    if (!htmlContent) return '';
    if (htmlContent.includes('{') && htmlContent.includes('}')) {
        htmlContent = normalizeAndMergeFirstLines(htmlContent);
    }
    htmlContent = normalizeSplitSignatures(htmlContent);

    // **Text** -> <strong>Text</strong>
    if (htmlContent.includes('**')) {
        htmlContent = htmlContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    }
    // __Text__ -> <u>Text</u>
    if (htmlContent.includes('__')) {
        htmlContent = htmlContent.replace(/__(.*?)__/g, '<u>$1</u>');
    }

    // Parse conditional signature directive: { => ! 0.9 0.5 VijayalakshmiSign.png [<<Arbitrator name>> == Ms. V. Vijayalakshmi ] }
    // Handles alignment both inside { ... } and outside => { ... }, as well as condition before or after signature
    const condSigRegex = /(?:(=&gt;|=>|&#61;&gt;|==|&lt;=|<=|&gt;|>|&lt;|<|-&gt;|->|=)\s*)?\{\s*(?:<[^>]+>)*\s*(?:(=&gt;|=>|&#61;&gt;|==|&lt;=|<=|&gt;|>|&lt;|<|-&gt;|->|=)\s*)?(?:<[^>]+>)*\s*!\s*(?:<[^>]+>)*\s*([^\s<]+)\s*(?:<[^>]+>)*\s+([^\s<]+)\s*(?:<[^>]+>)*\s+([a-zA-Z0-9_.-]+\.[a-zA-Z0-9]+|[a-zA-Z0-9_.-]*WhiteBlankImage[a-zA-Z0-9_.-]*|[a-zA-Z0-9_.-]+)\s*(?:(?:<[a-zA-Z0-9:-]+[^>]*>)*\[\s*([^[\\\]]+?)\s*\](?:<\/[a-zA-Z0-9:-]+>)?)\s*(?:<[^>]+>)*\s*\}/gi;

    htmlContent = htmlContent.replace(condSigRegex, (match, outerAlign, innerAlign, w, h, fn, condStr) => {
        const alignRaw = outerAlign || innerAlign;
        let align = 'right';
        if (alignRaw) {
            const a = alignRaw.replace(/&gt;/g, '>').replace(/&lt;/g, '<').trim();
            if (a === '==') align = 'center';
            else if (a === '<=' || a === '<') align = 'left';
            else if (a === '=>' || a === '>' || a === '->' || a === '=') align = 'right';
        }
        const cleanCond = cleanConditionSyntax(condStr);
        const { operator, rules } = parseConditionString(cleanCond);
        const ruleId = 'sig_rule_' + Date.now() + '_' + Math.random().toString(36).substring(7);
        // Encode < and > as &#60; and &#62; so downstream variable-tag replacer never mangles attribute contents
        const encodedCond = cleanCond.replace(/</g, '&#60;').replace(/>/g, '&#62;').replace(/"/g, '&quot;');
        const safeCondData = JSON.stringify({ operator, rules, label: cleanCond })
            .replace(/</g, '&#60;')
            .replace(/>/g, '&#62;')
            .replace(/"/g, '&quot;')
            .replace(/\{/g, '&#123;')
            .replace(/\}/g, '&#125;');
        return `<div data-type="white-blank-image" data-width="${w}" data-height="${h}" data-filename="${fn}" data-align="${align}" data-condition="${encodedCond}" data-condition-data="${safeCondData}" data-rule-id="${ruleId}"></div>`;
    });

    // Also support condition before signature: { [cond] => ! 0.9 0.5 filename.png }
    const condBeforeSigRegex = /\{\s*(?:<[a-zA-Z0-9:-]+[^>]*>)*\[\s*([^[\\\]]+?)\s*\](?:<\/[a-zA-Z0-9:-]+>)?\s*(?:(=&gt;|=>|&#61;&gt;|==|&lt;=|<=|&gt;|>|&lt;|<|-&gt;|->|=)\s*)?!\s*([^\s<]+)\s+([^\s<]+)\s+([a-zA-Z0-9_.-]+\.[a-zA-Z0-9]+|[a-zA-Z0-9_.-]*WhiteBlankImage[a-zA-Z0-9_.-]*|[a-zA-Z0-9_.-]+)\s*\}/gi;

    htmlContent = htmlContent.replace(condBeforeSigRegex, (match, condStr, alignRaw, w, h, fn) => {
        let align = 'right';
        if (alignRaw) {
            const a = alignRaw.replace(/&gt;/g, '>').replace(/&lt;/g, '<').trim();
            if (a === '==') align = 'center';
            else if (a === '<=' || a === '<') align = 'left';
            else if (a === '=>' || a === '>' || a === '->' || a === '=') align = 'right';
        }
        const cleanCond = cleanConditionSyntax(condStr);
        const { operator, rules } = parseConditionString(cleanCond);
        const ruleId = 'sig_rule_' + Date.now() + '_' + Math.random().toString(36).substring(7);
        const encodedCond = cleanCond.replace(/</g, '&#60;').replace(/>/g, '&#62;').replace(/"/g, '&quot;');
        const safeCondData = JSON.stringify({ operator, rules, label: cleanCond })
            .replace(/</g, '&#60;')
            .replace(/>/g, '&#62;')
            .replace(/"/g, '&quot;')
            .replace(/\{/g, '&#123;')
            .replace(/\}/g, '&#125;');
        return `<div data-type="white-blank-image" data-width="${w}" data-height="${h}" data-filename="${fn}" data-align="${align}" data-condition="${encodedCond}" data-condition-data="${safeCondData}" data-rule-id="${ruleId}"></div>`;
    });

    // Parse aligned signature directive: => ! 0.9 0.5 VijayalakshmiSign.png
    htmlContent = htmlContent.replace(/(?:(=&gt;|=>|&#61;&gt;|==|&lt;=|<=|&gt;|>|&lt;|<|-&gt;|->|=)\s*)!\s*([^\s<]+)\s+([^\s<]+)\s+([a-zA-Z0-9_.-]+\.[a-zA-Z0-9]+|[a-zA-Z0-9_.-]*WhiteBlankImage[a-zA-Z0-9_.-]*|[a-zA-Z0-9_.-]+(?=\s|$|<))/gi, (match, alignRaw, w, h, fn) => {
        let align = 'left';
        if (alignRaw) {
            const a = alignRaw.replace(/&gt;/g, '>').replace(/&lt;/g, '<').trim();
            if (a === '=>' || a === '>' || a === '->' || a === '=') align = 'right';
            else if (a === '==') align = 'center';
            else if (a === '<=' || a === '<') align = 'left';
        }
        return `<div data-type="white-blank-image" data-width="${w}" data-height="${h}" data-filename="${fn}" data-align="${align}"></div>`;
    });

    // Parse conditional & optional blocks:
    if (htmlContent.includes('{')) {
        let prevCurly;
        do {
            prevCurly = htmlContent;
            htmlContent = htmlContent.replace(/\{([^{}]+)\}/g, (match, inner) => {
                const bracketMatch = inner.match(/(?:<[a-zA-Z0-9:-]+[^>]*>)*\[\s*([^[\\\]]+?)\s*\](?:<\/[a-zA-Z0-9:-]+>)?/);
                if (bracketMatch && isConditionBracket(bracketMatch[1])) {
                    const condStr = bracketMatch[1];
                    const { operator, rules } = parseConditionString(condStr);
                    const ruleId = 'rule_' + Date.now() + '_' + Math.random().toString(36).substring(7);
                    
                    let content = inner.replace(bracketMatch[0], '').trim();
                    content = content.replace(/^;\s*/, '').replace(/^(?:=&gt;|=>|&#61;&gt;|==|&lt;=|<=|=)\s*/i, '');
                    
                    if (!content) {
                        content = '&nbsp;';
                    }

                    const firstRule = rules[0] || { variable: '?', operator: 'exists', value: '' };
                    const op = (firstRule.operator || 'exists').toLowerCase();
                    const isExists = op === 'exists' || op === 'is_not_empty';
                    const ruleLabel = isExists || !firstRule.value 
                        ? ('Condition: ' + (firstRule.variable || '?'))
                        : ('Condition: ' + (firstRule.variable || '?') + ' ' + firstRule.operator + ' ' + firstRule.value);
                    
                    const safeJson = JSON.stringify(rules).replace(/"/g, '&quot;').replace(/\{/g, '&#123;').replace(/\}/g, '&#125;');
                    return '<span data-conditional-block="" class="conditional-mark" data-rule-id="' + ruleId + '" data-operator="' + operator + '" data-rules="' + safeJson + '" data-conditions="' + safeJson + '" data-rule-label="' + ruleLabel + '">' + content + '</span>';
                } else {
                    let content = inner.trim();
                    content = content.replace(/^;\s*/, '').replace(/^(?:=&gt;|=>|&#61;&gt;|==|&lt;=|<=|=)\s*/i, '');
                    if (!content) {
                        content = '&nbsp;';
                    }
                    return '<span data-type="optional-block" class="optional-block">' + content + '</span>';
                }
            });
        } while (htmlContent !== prevCurly && htmlContent.includes('{') && /\{([^{}]+)\}/.test(htmlContent));
    }

    if (htmlContent.includes('@')) {
        htmlContent = htmlContent.replace(/@(.*?)@/g, '<span data-type="clear-space-block" class="clear-space-block">$1</span>');
    }
    if (htmlContent.includes('$')) {
        htmlContent = htmlContent.replace(/\$(.*?)\$/g, '<span data-type="dollar-block" class="dollar-block">$1</span>');
    }

    if (htmlContent.includes('<<') || htmlContent.includes('&lt;&lt;') || htmlContent.includes('«') || htmlContent.includes('&laquo;')) {
        htmlContent = htmlContent.replace(/(?:&lt;&lt;|<<|&laquo;|«)([\s\S]*?)(?:&gt;&gt;|>>|&raquo;|»)/g, (_, innerHtml) => {
            const isBold = /^\s*\*\*/.test(innerHtml) || /\*\*\s*$/.test(innerHtml) || innerHtml.includes('**');
            let text = innerHtml
                .replace(/<[^>]+>/g, '')
                .replace(/&amp;/g, '&')
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&quot;/g, '"')
                .replace(/&#39;|&apos;/g, "'")
                .replace(/\*\*/g, '')
                .replace(/&nbsp;/g, ' ')
                .replace(/\u00a0/g, ' ')
                .trim();
            if (!text)
                return '';
            if (text.includes('<<') || text.includes('&lt;&lt;') || text.includes('«') || text.includes('&laquo;')) {
                const parts = text.split(/(?:&lt;&lt;|<<|&laquo;|«)/);
                return parts.map((p, idx) => {
                    let sep = '';
                    let name = p;
                    if (idx === 0 && name.includes('/')) {
                        const slashIdx = name.lastIndexOf('/');
                        sep = name.substring(slashIdx);
                        name = name.substring(0, slashIdx).trim();
                    }
                    name = name
                        .replace(/&amp;/g, '&')
                        .replace(/&lt;/g, '<')
                        .replace(/&gt;/g, '>')
                        .replace(/&quot;/g, '"')
                        .replace(/&#39;|&apos;/g, "'")
                        .replace(/(?:&gt;&gt;|>>|&raquo;|»)/g, '')
                        .replace(/\*\*/g, '')
                        .replace(/&nbsp;/g, ' ')
                        .replace(/\u00a0/g, ' ')
                        .trim();
                    if (!name)
                        return '';
                    const key = name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
                    const escapedName = name.replace(/"/g, '&quot;');
                    const tag = `<span data-variable-tag data-variable-key="${key}" data-label="${escapedName}"></span>${sep}`;
                    return isBold ? `<strong>${tag}</strong>` : tag;
                }).join('');
            }
            const cleanKey = text.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
            const escapedText = text.replace(/"/g, '&quot;');
            const tag = `<span data-variable-tag data-variable-key="${cleanKey}" data-label="${escapedText}"></span>`;
            return isBold ? `<strong>${tag}</strong>` : tag;
        });
    }

    htmlContent = htmlContent.replace(/(?:&lt;|<)(?:page break|bage break)(?:&gt;|>)|-page break-|-bage break-/gi, '<div data-type="page-break"></div>');
    htmlContent = htmlContent.replace(/(?:&lt;|<)line break(?:&gt;|>)|-line break-/gi, '<div data-type="line-break"></div>');
    htmlContent = htmlContent.replace(/(?:&lt;|<)line(?:&gt;|>)|-line-/gi, '<div data-type="horizontal-line"></div>');
    htmlContent = htmlContent.replace(/!\s*([^\s<]+)\s+([^\s<]+)\s+([a-zA-Z0-9_.-]+\.[a-zA-Z0-9]+|[a-zA-Z0-9_.-]*WhiteBlankImage[a-zA-Z0-9_.-]*|[a-zA-Z0-9_.-]+(?=\s|$|<))/gi, '<div data-type="white-blank-image" data-width="$1" data-height="$2" data-filename="$3"></div>');

    return htmlContent;
}

const getAlignment = (cellText) => {
    const t = (cellText || '').replace(/<[^>]+>/g, '').trim();
    if (t === '==' || t === '=') return 'center';
    if (t === '=>' || t === '=&gt;' || t === '>') return 'right';
    if (t === '<=' || t === '&lt;=' || t === '<') return 'left';
    return null;
};

const isAlignmentRow = (cells) => {
    return cells.length > 0 && cells.every(c => getAlignment(c) !== null);
};

const isHeaderRow = (cells) => {
    if (!cells || cells.length === 0) return false;

    // 1. If first cell is a pure number (e.g. "1", "2", "3"), it's definitely a data row, not a column header!
    const firstRaw = (cells[0] || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ').trim();
    if (/^\d+$/.test(firstRaw)) return false;

    // 2. If any cell has variable tags, it's a data row, not a column header!
    const hasVariables = cells.some(c => /(?:&lt;&lt;|<<|&laquo;)[^<>&]+(?:&gt;&gt;|>>|&raquo;)/i.test(c) || /data-variable/i.test(c));
    if (hasVariables) return false;

    const first = firstRaw.toLowerCase();
    const isFirstHeader = ['s.no.', 's.no', 'sl.no.', 'sl.no', 'sr.no.', 'sr.no', 'no.', 'no', '#', 'sno', 's. no.', 's. no', 'sl no', 'sr no'].includes(first);
    if (isFirstHeader) return true;

    const textCells = cells.map(c => c.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ').trim().toLowerCase()).filter(Boolean);
    const headerKeywords = [
        'date', 'index', 'exhibits', 'exhibit', 'description', 'particulars', 'amount', 'details', 'name', 
        'documents', 'document', 'annexure', 'boundary side', 'boundary', 'loan', 'number', 'tenure', 
        'interest', 'rate', 'balance', 'principal', 'borrower', 'guarantor', 'status', 'account', 'branch', 
        'bank', 'item', 'type', 'value', 'period', 'schedule', 'property'
    ];
    const matchCount = textCells.filter(c => headerKeywords.some(k => c === k || c.startsWith(k + ' ') || c.endsWith(' ' + k) || c.includes(' ' + k + ' '))).length;
    const minMatches = cells.length <= 2 ? 1 : 2;
    return matchCount >= minMatches;
};

const isAlignMarker = (str) => {
    if (!str) return true;
    const c = str.replace(/<[^>]+>/g, '').replace(/&lt;|&gt;|<|>|\[|\]|\{|\}/g, '').trim().toLowerCase();
    if (/[a-zA-Z0-9]/.test(c)) return false;
    return !c || ['>', '<', '=>', '<=', '==', '=', '=&gt;', '&lt;=', '&gt;', '&lt;'].includes(c) || /^[\s><=\[\]\{\}|&.,;:\-_]+$/.test(c);
};

export const extractCellsFromPipeRow = (elOrContent) => {
    let inner = typeof elOrContent === 'string' ? elOrContent : (elOrContent.innerHTML || '');
    inner = inner.trim();
    let rowCondition = '';

    const condMatch = inner.match(/\[\s*(.*?)\s*\]\s*\}*$/);
    if (condMatch) {
        const rawCond = condMatch[1].replace(/^Condition:\s*/i, '').trim();
        const cleanKey = rawCond.replace(/&lt;|&gt;|<|>|\[|\]|\{|\}/g, '').trim();
        if (cleanKey && !isAlignMarker(cleanKey)) {
            if (cleanKey.includes('==')) {
                const parts = cleanKey.split(/\s*==\s*/);
                rowCondition = `[<<${parts[0].replace(/<<|>>/g, '').trim()}>> == ${parts[1].trim()}]`;
            } else {
                rowCondition = `[<<${cleanKey.replace(/<<|>>/g, '').trim()}>>]`;
            }
        }
        inner = inner.replace(/\[\s*.*?\s*\]\s*\}*$/, '').trim();
    }

    inner = inner.replace(/^\{+/, '').replace(/\}+$/, '').trim();
    inner = inner.replace(/^(?:\s|&nbsp;|<br\s*\/?>|<\/?[a-z0-9]+[^>]*>)*\|/i, '');
    inner = inner.replace(/\|(?:\s|&nbsp;|<br\s*\/?>|<\/?[a-z0-9]+[^>]*>)*$/i, '');

    let rawCells = inner.split('|').map(c => c.trim());
    const isAllEmpty = rawCells.every(c => !c.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ').trim());
    if (!isAllEmpty) {
        while (rawCells.length > 0 && !rawCells[rawCells.length - 1].replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ').trim()) {
            rawCells.pop();
        }
    }

    return { cells: rawCells, rowCondition };
};


/**
 * Removes duplicate HTML tables or duplicate table lines in raw HTML before block parsing.
 */
export function removeDuplicateTablesAndLines(html) {
    if (!html || typeof html !== 'string') return html;

    // Detect pipe table headers / column sets in the HTML
    const pipeRowRegex = /<p[^>]*>\s*\|(.*?)\|\s*<\/p>/gi;
    const pipeHeaderRows = [];
    let match;

    while ((match = pipeRowRegex.exec(html)) !== null) {
        const rowStr = match[1];
        const cells = rowStr.split('|').map(c => c.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/[\u200B-\u200D\uFEFF]/g, '').trim().toLowerCase()).filter(Boolean);
        const isAlign = cells.length > 0 && cells.every(c => ['==', '<=', '=>', '=&gt;', '&lt;='].includes(c));
        if (isAlign) continue;
        if (cells.length >= 2) {
            pipeHeaderRows.push(cells);
        }
    }

    // HTML tables should not be globally stripped matching pipe headers or other tables.
    // Any adjacent duplicate tables will be safely handled by deduplicateTablesInHtml.

    return html;
}

export function convertHtmlTablesToPipes(html) {
    if (!html || typeof html !== 'string' || !html.includes('<table')) {
        return html;
    }

    const tableRegex = /<table\b((?:[^>"']|"[^"]*"|'[^']*')*)>([\s\S]*?)<\/table>/gi;
    return html.replace(tableRegex, (match, tableAttrs, innerTable) => {
        const tableCondMatch = tableAttrs.match(/data-table-condition(?:-data)?="([^"]+)"/i);
        const tableCond = tableCondMatch ? tableCondMatch[1].trim() : '';

        const rowRegex = /<tr\b((?:[^>"']|"[^"]*"|'[^']*')*)>([\s\S]*?)<\/tr>/gi;
        const rows = [];
        let rowMatch;
        let colAlignments = [];

        while ((rowMatch = rowRegex.exec(innerTable)) !== null) {
            const rowAttrs = rowMatch[1] || '';
            const rowInner = rowMatch[2] || '';

            const rowCondMatch = rowAttrs.match(/data-row-condition="([^"]+)"/i);
            const rowCond = rowCondMatch ? rowCondMatch[1].trim() : '';

            const cellRegex = /<(?:td|th)\b((?:[^>"']|"[^"]*"|'[^']*')*)>([\s\S]*?)<\/(?:td|th)>/gi;
            const cells = [];
            let cellMatch;
            let cellIdx = 0;

            while ((cellMatch = cellRegex.exec(rowInner)) !== null) {
                const cellAttrs = cellMatch[1] || '';
                let cellContent = cellMatch[2] || '';

                if (!colAlignments[cellIdx]) {
                    const alignMatch = cellAttrs.match(/text-align\s*:\s*(right|center|left)/i) ||
                                       cellAttrs.match(/align="([^"]+)"/i) ||
                                       cellContent.match(/text-align\s*:\s*(right|center|left)/i);
                    if (alignMatch) {
                        const val = alignMatch[1].toLowerCase();
                        if (val === 'right' || val === 'end') colAlignments[cellIdx] = '=>';
                        else if (val === 'center') colAlignments[cellIdx] = '==';
                        else if (val === 'left' || val === 'start') colAlignments[cellIdx] = '<=';
                    }
                }

                // If cell already has variable tag, restore <<label>>
                cellContent = cellContent.replace(/<span\b[^>]*data-label="([^"]+)"[^>]*>.*?<\/span>/gi, '<<$1>>');

                // 1. Convert <br>, </p><p>, newlines into spaces
                cellContent = cellContent
                    .replace(/<\/?p\b[^>]*>/gi, ' ')
                    .replace(/<br\s*\/?>/gi, ' ')
                    .replace(/<\/?div\b[^>]*>/gi, ' ')
                    .replace(/\r?\n/g, ' ');

                // 2. Normalize whitespace and non-breaking spaces
                cellContent = cellContent
                    .replace(/&nbsp;/g, ' ')
                    .replace(/\u00a0/g, ' ')
                    .replace(/[\u200B-\u200D\uFEFF]/g, '')
                    .replace(/\s+/g, ' ')
                    .trim();

                // 3. Fix broken / split variable tags inside cells
                cellContent = cellContent.replace(/(?:&lt;&lt;|<<|&laquo;)\s*([^<>&]+?)\s*(?:&gt;&gt;|>>|&raquo;)/g, (m, varName) => {
                    return `<<${varName.trim()}>>`;
                });

                // 4. Protect literal pipe characters inside cell text
                cellContent = cellContent.replace(/(?<!\|)\|(?!\|)/g, '&#124;');

                cells.push(cellContent || ' ');
                cellIdx++;
            }

            if (cells.length > 0) {
                rows.push({ cells, rowCond });
            }
        }

        if (rows.length === 0) return '';

        const lines = [];
        if (tableCond) {
            lines.push(`<p>{<<${tableCond}>>}</p>`);
        }

        const filteredRows = rows.filter(r => !r.cells.every(c => {
            const t = (c || '').replace(/<[^>]+>/g, '').trim();
            return t === '<=' || t === '==' || t === '=>' || t === '&lt;=' || t === '=&gt;';
        }));
        const maxCols = Math.max(...filteredRows.map(r => r.cells.length), colAlignments.length, 1);

        const alignCells = [];
        for (let c = 0; c < maxCols; c++) {
            alignCells.push(colAlignments[c] || '<=');
        }
        lines.push(`<p>|${alignCells.join('|')}|</p>`);

        for (let r = 0; r < filteredRows.length; r++) {
            const { cells, rowCond } = filteredRows[r];
            let pipeLine = '|' + cells.join('|') + '|';
            if (rowCond) {
                const cleanKey = rowCond.replace(/^Condition:\s*/i, '').replace(/&lt;|&gt;|<|>|\[|\]|\{|\}/g, '').trim();
                pipeLine = `{|${cells.join('|')}| [<<${cleanKey}>>] }`;
            }
            lines.push(`<p>${pipeLine}</p>`);
        }

        return '\n' + lines.join('\n') + '\n';
    });
}

export function parseTemplateSyntaxToHtml(html) {
    if (!html) return '';

    // Convert Word tables into pipe tables first so cell whitespace and line breaks are preserved
    html = convertHtmlTablesToPipes(html);

    // Strip incoming Word / Mammoth native bold tags so ONLY explicit template syntax **text** is considered bold
    html = html.replace(/<\/?(?:strong|b)\b[^>]*>/gi, '');
    html = html.replace(/<</g, '&lt;&lt;').replace(/>>/g, '&gt;&gt;');
    html = normalizeAndMergeFirstLines(html);
    html = normalizeSplitSignatures(html);
    html = removeDuplicateTablesAndLines(html);

    // If a paragraph/div contains multiple pipe lines separated by <br> or newlines, split them into separate blocks
    html = html.replace(/<(p|div)\b([^>]*)>([\s\S]*?)<\/\1>/gi, (match, tag, attrs, content) => {
        if (!content.includes('|')) return match;
        if (/<br\s*\/?>|\r?\n/i.test(content)) {
            const parts = content.split(/<br\s*\/?>|\r?\n/gi);
            if (parts.length > 1 && parts.some(l => {
                const plain = l.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ').trim();
                const stripped = plain.replace(/^\{+/, '').replace(/\}+$/, '').trim();
                return (stripped.startsWith('|') || plain.startsWith('|')) && stripped.includes('|');
            })) {
                const attrStr = attrs ? ` ${attrs}` : '';
                return parts
                    .map(l => l.trim())
                    .filter(Boolean)
                    .map(l => `<${tag}${attrStr}>${l}</${tag}>`)
                    .join('');
            }
        }
        return match;
    });

    if (!/<(?:p|div|h[1-6]|li)\b/i.test(html) && html.includes('\n')) {
        html = html.split(/\r?\n/).map(line => `<p>${line || ''}</p>`).join('');
    }

    // Split into block elements: <p>...</p>, <div>...</div>, <h1>...</h1>, etc.
    const blockRegex = /<(p|div|h[1-6]|li)\b([^>]*)>([\s\S]*?)<\/\1>/gi;
    const blocks = [];
    let match;

    while ((match = blockRegex.exec(html)) !== null) {
        const [full, tag, attrs, content] = match;
        const text = content.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ').trim();
        const strippedText = text.replace(/^\{+/, '').replace(/\}+$/, '').trim();
        const isPipe = (strippedText.startsWith('|') || text.startsWith('|')) && strippedText.includes('|') && strippedText.split('|').length >= 3;
        const isPageBreak = /^(?:<page break>|&lt;page break&gt;|-page break-|page break)$/i.test(text) || (attrs && attrs.includes('page-break'));
        const isEmpty = !text;

        blocks.push({
            tag: tag.toLowerCase(),
            attrs: attrs || '',
            content,
            text,
            isPipe,
            isPageBreak,
            isEmpty
        });
    }

    // If no HTML block tags were matched, wrap input in a block
    if (blocks.length === 0 && html.trim()) {
        const text = html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ').trim();
        const strippedText = text.replace(/^\{+/, '').replace(/\}+$/, '').trim();
        blocks.push({
            tag: 'p',
            attrs: '',
            content: html,
            text,
            isPipe: (strippedText.startsWith('|') || text.startsWith('|')) && strippedText.includes('|') && strippedText.split('|').length >= 3,
            isPageBreak: false,
            isEmpty: !text
        });
    }

    const output = [];
    let pendingAlign = null;

    for (let i = 0; i < blocks.length; i++) {
        const b = blocks[i];

        // 1. Margin certificate annotation check
        if (/^(?:e-?stamp\s+)?certificate\s+(?:no|number)[\s.:]*[A-Z0-9\-_/\s]+$/i.test(b.text) ||
            /^certificate\s+no\s*[:.]\s*in-[a-z0-9]+/i.test(b.text)) {
            continue;
        }

        // 2. Standalone alignment and heading markers
        if (!b.isPipe && !b.isEmpty) {
            if (/^(?:&lt;=|<=\s*)$/i.test(b.text)) { pendingAlign = { type: 'align', align: 'left' }; continue; }
            if (/^(?:=&gt;|=>\s*)$/i.test(b.text)) { pendingAlign = { type: 'align', align: 'right' }; continue; }
            if (/^(?:==\s*)$/.test(b.text)) { pendingAlign = { type: 'align', align: 'center' }; continue; }
            if (/^###\s*$/.test(b.text)) { pendingAlign = { type: 'heading', level: 3 }; continue; }
            if (/^#\s*$/.test(b.text)) { pendingAlign = { type: 'heading', level: 1 }; continue; }
        }

        // 3. Pipe table grouping
        if (b.isPipe) {
            const tableRows = [];
            let rIdx = i;

            while (rIdx < blocks.length) {
                const curB = blocks[rIdx];
                if (curB.isPipe) {
                    const { cells, rowCondition } = extractCellsFromPipeRow(curB.content);
                    const hasDataRows = tableRows.some(r => !isAlignmentRow(r.cells) && !isHeaderRow(r.cells));

                    if (hasDataRows && (isAlignmentRow(cells) || isHeaderRow(cells))) {
                        // A new alignment row or header row after data rows indicates a new table!
                        break;
                    }
                    tableRows.push({ cells, rowCondition });
                    rIdx++;
                } else if (curB.isEmpty || curB.isPageBreak) {
                    let lookahead = rIdx;
                    let emptyCount = 0;
                    let pageBreakCount = 0;
                    while (lookahead < blocks.length && (blocks[lookahead].isEmpty || blocks[lookahead].isPageBreak)) {
                        if (blocks[lookahead].isPageBreak) {
                            pageBreakCount++;
                        } else {
                            emptyCount++;
                            const brs = (blocks[lookahead].content.match(/<br\s*\/?>/gi) || []).length;
                            if (brs > 1) {
                                emptyCount += (brs - 1);
                            }
                        }
                        lookahead++;
                    }

                    // If there is more than one line space between tables, consider it as a different table
                    if (emptyCount > 1) {
                        break;
                    }

                    if (lookahead < blocks.length && blocks[lookahead].isPipe) {
                        const { cells: nextCells } = extractCellsFromPipeRow(blocks[lookahead].content);
                        const hasDataRows = tableRows.some(r => !isAlignmentRow(r.cells) && !isHeaderRow(r.cells));

                        // If the next row is an alignment row or header row, or if table already has data rows and numbering restarts:
                        if (isAlignmentRow(nextCells)) {
                            break;
                        }

                        if (isHeaderRow(nextCells)) {
                            // Check if it's a repeated identical header across page break
                            const firstHeaderRow = tableRows.find(r => isHeaderRow(r.cells))?.cells || tableRows[0]?.cells || [];
                            const isDuplicateHeader = firstHeaderRow.length > 0 &&
                                firstHeaderRow.length === nextCells.length &&
                                firstHeaderRow.every((c, idx) => {
                                    const t1 = (c || '').replace(/<[^>]+>/g, '').trim().toLowerCase();
                                    const t2 = (nextCells[idx] || '').replace(/<[^>]+>/g, '').trim().toLowerCase();
                                    return t1 === t2;
                                });

                            // Only bridge if it's across a page break with identical header AND not restarting numbering at 1
                            if (pageBreakCount > 0 && isDuplicateHeader && emptyCount <= 1) {
                                let isRestart = false;
                                if (lookahead + 1 < blocks.length && blocks[lookahead + 1].isPipe) {
                                    const { cells: afterHeaderCells } = extractCellsFromPipeRow(blocks[lookahead + 1].content);
                                    const firstCellVal = (afterHeaderCells[0] || '').replace(/<[^>]+>/g, '').trim();
                                    if (firstCellVal === '1' && hasDataRows) {
                                        isRestart = true;
                                    }
                                }
                                if (isRestart) {
                                    break;
                                }
                                rIdx = lookahead + 1;
                                continue;
                            } else {
                                break;
                            }
                        }

                        // If empty line (emptyCount >= 1) without a page break, check if column count differs or numbering restarts at 1
                        if (emptyCount >= 1) {
                            const firstRowCells = tableRows[0]?.cells || [];
                            if (firstRowCells.length > 0 && nextCells.length !== firstRowCells.length) {
                                break;
                            }
                            const nextFirstCell = (nextCells[0] || '').replace(/<[^>]+>/g, '').trim();
                            if (nextFirstCell === '1' && hasDataRows) {
                                break;
                            }
                        }

                        rIdx = lookahead;
                    } else {
                        break;
                    }
                } else {
                    break;
                }
            }

            i = rIdx - 1; // Advance outer loop

            // Determine column alignments and extract data rows
            let colAlignments = [];
            const dataRows = [];
            let commonCondition = '';

            for (let r = 0; r < tableRows.length; r++) {
                const { cells, rowCondition } = tableRows[r];
                if (isAlignmentRow(cells)) {
                    colAlignments = cells.map(c => getAlignment(c) || '');
                } else {
                    if (rowCondition && !commonCondition) {
                        commonCondition = rowCondition;
                    }
                    dataRows.push({ cells, rowCondition });
                }
            }

            // Remove trailing empty columns across all data rows ONLY if the table has data in at least one column
            let maxCols = Math.max(...dataRows.map(r => r.cells.length), colAlignments.length, 1);
            const hasAnyData = dataRows.some(r => r.cells.some(c => {
                const plain = (c || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ').trim();
                return plain.length > 0;
            }));
            if (hasAnyData) {
                const minCols = Math.max(colAlignments.length, 1);
                for (let c = maxCols - 1; c >= minCols; c--) {
                    const isColEmpty = dataRows.every(r => {
                        const cell = r.cells[c] || '';
                        const plain = cell.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ').trim();
                        return !plain;
                    });
                    if (isColEmpty) {
                        dataRows.forEach(r => {
                            if (r.cells.length > c) r.cells.splice(c, 1);
                        });
                        if (colAlignments.length > c) colAlignments.splice(c, 1);
                        maxCols--;
                    } else {
                        break;
                    }
                }
            }

            const condKey = commonCondition.replace(/&lt;|&gt;|<|>|\[|\]|\{|\}/g, '').trim();
            const isCommonAlignMarker = isAlignMarker(commonCondition) || isAlignMarker(condKey);

            let hasConditionAlready = false;
            if (output.length > 0) {
                const prev = output[output.length - 1].toLowerCase();
                const condKeyLower = condKey.toLowerCase();
                if ((condKeyLower && prev.includes(condKeyLower)) || prev.includes('condition:') || prev.includes('data-conditional-block')) {
                    hasConditionAlready = true;
                }
            }

            if (commonCondition && condKey && !isCommonAlignMarker && !hasConditionAlready) {
                output.push(`<p>{<<${condKey}>>}</p>`);
            }

            const tableCondAttr = (commonCondition && !isCommonAlignMarker && !hasConditionAlready)
                ? ` data-table-condition="${commonCondition}"`
                : '';

            let tableHtml = `<table style="width: 100%; border-collapse: collapse; margin: 14px 0;"${tableCondAttr}><tbody>`;
            for (let r = 0; r < dataRows.length; r++) {
                const rowObj = dataRows[r];
                const isHeader = r === 0 && isHeaderRow(rowObj.cells);
                const tag = isHeader ? 'th' : 'td';
                const rowCondAttr = rowObj.rowCondition ? ` data-row-condition="${rowObj.rowCondition}"` : '';
                tableHtml += `<tr${rowCondAttr}>`;
                for (let c = 0; c < maxCols; c++) {
                    const rawCell = rowObj.cells[c] || '';
                    const cellHtml = processInlineSyntax(rawCell);
                    const rawAlign = colAlignments[c];
                    const cellAlign = (rawAlign && rawAlign.trim()) ? rawAlign.trim() : 'left';
                    const alignStyle = `text-align: ${cellAlign};`;
                    const cellStyle = `border: 1px solid #ccc; padding: 8px; vertical-align: top; word-break: break-word; overflow-wrap: break-word; box-sizing: border-box; text-align: ${cellAlign};`;
                    const pStyle = `margin: 0; line-height: 1.5; word-break: break-word; overflow-wrap: break-word; text-align: ${cellAlign};`;
                    const pContent = cellHtml 
                        ? `<p style="${pStyle}">${cellHtml}</p>` 
                        : `<p style="${pStyle}"><br></p>`;
                    tableHtml += `<${tag} style="${cellStyle}">${pContent}</${tag}>`;
                }
                tableHtml += `</tr>`;
            }
            tableHtml += `</tbody></table>`;
            output.push(tableHtml);

            // Check if subsequent blocks repeat the table headers and cells (duplicate table content lines)
            const tableHeaders = dataRows[0]?.cells.map(c => c.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/[\u200B-\u200D\uFEFF]/g, '').trim().toLowerCase()).filter(Boolean) || [];
            if (tableHeaders.length > 0 && i + 1 < blocks.length) {
                let nextIdx = i + 1;
                const nextBlock = blocks[nextIdx];
                if (!nextBlock.isPipe && !nextBlock.isPageBreak && !nextBlock.tag.startsWith('h') && !nextBlock.text.startsWith('#') && !nextBlock.text.startsWith('{')) {
                    const nextText = nextBlock.text.trim().toLowerCase();
                    if (nextText === tableHeaders[0] || (tableHeaders.includes(nextText) && nextIdx + 1 < blocks.length && tableHeaders.includes(blocks[nextIdx + 1].text.trim().toLowerCase()))) {
                        while (nextIdx < blocks.length) {
                            const curBlock = blocks[nextIdx];
                            if (curBlock.isPipe || curBlock.isPageBreak || curBlock.tag.startsWith('h') || curBlock.text.startsWith('#') || curBlock.text.startsWith('{')) {
                                break;
                            }
                            const curText = curBlock.text.trim().toLowerCase();
                            const isHdr = tableHeaders.includes(curText);
                            const isNum = /^\d+$/.test(curText);
                            const isVar = /^<<.*?>>$/.test(curText) || /^\[.*?\]$/.test(curText) || curText.startsWith('&lt;&lt;');
                            const isBlank = !curText;
                            if (isHdr || isNum || isVar || isBlank) {
                                nextIdx++;
                            } else {
                                break;
                            }
                        }
                        if (nextIdx > i + 1) {
                            console.log(`[Parser] Skipped ${nextIdx - (i + 1)} duplicate table lines following table.`);
                            i = nextIdx - 1;
                        }
                    }
                }
            }

            pendingAlign = null;
            continue;
        }

        // 4. Normal block elements
        if (b.isEmpty) {
            let styleStr = '';
            if (b.attrs) {
                const styleMatch = b.attrs.match(/style="([^"]*)"/i);
                if (styleMatch) {
                    styleStr = styleMatch[1].replace(/line-height:\s*[^;]+;?/gi, '').trim();
                }
            }
            const attrStr = styleStr ? ` style="${styleStr}"` : '';
            output.push(`<p${attrStr}><br></p>`);
            continue;
        }

        let processedContent = processInlineSyntax(b.content);
        processedContent = processedContent.replace(/^\s*;\s*/, '');

        // Standalone ## is used by user to print empty space in notice - preserve visibly in editor
        if (b.text.trim() === '##') {
            output.push('<p>##</p>');
            continue;
        }

        // Check heading markdown prefixes
        if (/^###\s*/.test(b.text) || (pendingAlign && pendingAlign.type === 'heading' && pendingAlign.level === 3)) {
            processedContent = processedContent.replace(/^\s*###\s*/, '');
            output.push(`<h3 style="text-align: center; font-weight: bold; text-decoration: underline; font-size: 1em;">${processedContent}</h3>`);
            pendingAlign = null;
            continue;
        }
        if (/^##\s*\S/.test(b.text) || (pendingAlign && pendingAlign.type === 'heading' && pendingAlign.level === 2)) {
            processedContent = processedContent.replace(/^\s*##\s*/, '');
            output.push(`<h2 style="text-align: center; font-weight: bold; font-size: 1em;">${processedContent}</h2>`);
            pendingAlign = null;
            continue;
        }
        if (/^#\s*/.test(b.text) || (pendingAlign && pendingAlign.type === 'heading' && pendingAlign.level === 1)) {
            processedContent = processedContent.replace(/^\s*#\s*/, '');
            output.push(`<h1 style="text-align: center; font-weight: normal; text-decoration: none; font-size: 1em;">${processedContent}</h1>`);
            pendingAlign = null;
            continue;
        }

        // Check alignment prefixes or imported styles
        let blockAlign = '';
        if (/^(?:=&gt;|=>|&#61;&gt;)\s*/i.test(b.text)) {
            processedContent = processedContent.replace(/^\s*(?:=&gt;|=>|&#61;&gt;)\s*/i, '');
            blockAlign = 'right';
        } else if (/^(?:==)\s*/.test(b.text)) {
            processedContent = processedContent.replace(/^\s*(?:==)\s*/, '').replace(/\s*==\s*$/, '');
            blockAlign = 'center';
        } else if (/^(?:&lt;=|<=)\s*/i.test(b.text)) {
            processedContent = processedContent.replace(/^\s*(?:&lt;=|<=)\s*/i, '');
            blockAlign = 'left';
        } else if (pendingAlign && pendingAlign.type === 'align') {
            blockAlign = pendingAlign.align;
        }

        pendingAlign = null;

        // Special full-block match (page break, line break, horizontal line, signature box)
        const matchSpecial = processedContent.trim().match(/^<div data-type="(page-break|line-break|horizontal-line|white-blank-image)"(.*?)><\/div>$/);
        if (matchSpecial) {
            let specialAttrs = matchSpecial[2];
            if (blockAlign && !specialAttrs.includes('data-align=')) {
                specialAttrs += ` data-align="${blockAlign}"`;
            }
            output.push(`<div data-type="${matchSpecial[1]}"${specialAttrs}></div>`);
            continue;
        }

        // Maintain existing tag or paragraph
        let outTag = 'p';
        if (['h1', 'h2', 'h3'].includes(b.tag)) {
            outTag = b.tag;
        }

        // Merge style attributes
        let styleStr = '';
        if (b.attrs) {
            const styleMatch = b.attrs.match(/style="([^"]*)"/i);
            if (styleMatch) {
                styleStr = styleMatch[1];
            }
        }
        if (styleStr) {
            // Strip any tight or pt-based line-height to ensure uniform 1.5 spacing without overlap
            styleStr = styleStr.replace(/line-height:\s*[^;]+;?/gi, '').trim();
        }
        if (blockAlign) {
            if (styleStr) {
                styleStr = styleStr.replace(/text-align:\s*[^;]+/i, '').trim();
                styleStr = `text-align: ${blockAlign}; ${styleStr}`.trim();
            } else {
                styleStr = `text-align: ${blockAlign};`;
            }
        }

        const attrStr = styleStr ? ` style="${styleStr}"` : '';
        output.push(`<${outTag}${attrStr}>${processedContent}</${outTag}>`);
    }

    return deduplicateTablesInHtml(output.join('\n'));
}

/**
 * Computes a normalized structural fingerprint of an HTML table (DOM element or HTML string).
 * Normalizes all cell values (lowercase, trimmed whitespace, zero-width spaces stripped, trailing blanks removed).
 */
export function extractTableFingerprint(tableHtmlOrEl) {
    if (!tableHtmlOrEl) return '';

    // If it's a DOM element (browser environment)
    if (typeof tableHtmlOrEl === 'object' && tableHtmlOrEl.querySelectorAll) {
        const tableCond = (tableHtmlOrEl.getAttribute('data-table-condition') || tableHtmlOrEl.getAttribute('data-table-condition-data') || '').trim().toLowerCase();
        const rows = Array.from(tableHtmlOrEl.querySelectorAll('tr'));
        const rowSignatures = [];
        let totalCells = 0;
        for (const tr of rows) {
            const rowCond = (tr.getAttribute('data-row-condition') || '').trim().toLowerCase();
            const cells = Array.from(tr.querySelectorAll('th, td'));
            const cellTexts = cells.map(cell => {
                totalCells++;
                const varTags = Array.from(cell.querySelectorAll('[data-variable-tag], [data-label], [data-variable-key]'));
                const vars = varTags.map(el => (el.getAttribute('data-label') || el.getAttribute('data-variable-key') || el.textContent || '').trim().toLowerCase()).filter(Boolean);
                const condTags = Array.from(cell.querySelectorAll('[data-rule-id], [data-rules], [data-conditions]'));
                const conds = condTags.map(el => (el.getAttribute('data-rule-id') || el.getAttribute('data-rules') || '').trim().toLowerCase()).filter(Boolean);
                const text = (cell.textContent || '')
                    .replace(/[\u200B-\u200D\uFEFF]/g, '')
                    .replace(/&nbsp;/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim()
                    .toLowerCase();
                return [text, vars.length ? 'var(' + vars.join(',') + ')' : '', conds.length ? 'cond(' + conds.join(',') + ')' : ''].filter(Boolean).join('::');
            });
            while (cellTexts.length > 1 && cellTexts[cellTexts.length - 1] === '') {
                cellTexts.pop();
            }
            if (cellTexts.some(t => t.length > 0) || rowCond) {
                rowSignatures.push((rowCond ? 'rowCond(' + rowCond + ')::' : '') + cellTexts.join(':::'));
            }
        }
        if (rowSignatures.length === 0 && rows.length > 0) {
            return `empty_table_${rows.length}x${totalCells}`;
        }
        return (tableCond ? 'tblCond(' + tableCond + ')###' : '') + rowSignatures.join('###');
    }

    // If it's an HTML string
    const tableCondMatch = tableHtmlOrEl.match(/data-table-condition(?:-data)?="([^"]+)"/i);
    const tableCond = tableCondMatch ? tableCondMatch[1].trim().toLowerCase() : '';
    const rowRegex = /<tr\b([^>]*)>([\s\S]*?)<\/tr>/gi;
    const rows = [];
    let rowMatch;
    let totalRows = 0;
    let totalCells = 0;
    while ((rowMatch = rowRegex.exec(tableHtmlOrEl)) !== null) {
        totalRows++;
        const rowAttrs = rowMatch[1] || '';
        const rowCondMatch = rowAttrs.match(/data-row-condition="([^"]+)"/i);
        const rowCond = rowCondMatch ? rowCondMatch[1].trim().toLowerCase() : '';
        const cellRegex = /<(?:td|th)\b[^>]*>([\s\S]*?)<\/(?:td|th)>/gi;
        const cells = [];
        let cellMatch;
        while ((cellMatch = cellRegex.exec(rowMatch[2])) !== null) {
            totalCells++;
            const cellHtml = cellMatch[1];
            const vars = [];
            const varRegex = /(?:data-label="([^"]+)"|data-variable-key="([^"]+)"|<<([A-Za-z0-9_\s/-]+)>>)/gi;
            let vm;
            while ((vm = varRegex.exec(cellHtml)) !== null) {
                vars.push((vm[1] || vm[2] || vm[3]).trim().toLowerCase());
            }
            const conds = [];
            const condRegex = /data-rule-id="([^"]+)"|data-rules="([^"]+)"/gi;
            let cm;
            while ((cm = condRegex.exec(cellHtml)) !== null) {
                conds.push((cm[1] || cm[2]).trim().toLowerCase());
            }
            const text = cellHtml
                .replace(/<[^>]+>/g, '')
                .replace(/&nbsp;/g, ' ')
                .replace(/[\u200B-\u200D\uFEFF]/g, '')
                .replace(/\s+/g, ' ')
                .trim()
                .toLowerCase();
            cells.push([text, vars.length ? 'var(' + vars.join(',') + ')' : '', conds.length ? 'cond(' + conds.join(',') + ')' : ''].filter(Boolean).join('::'));
        }
        while (cells.length > 1 && cells[cells.length - 1] === '') {
            cells.pop();
        }
        if (cells.some(t => t.length > 0) || rowCond) {
            rows.push((rowCond ? 'rowCond(' + rowCond + ')::' : '') + cells.join(':::'));
        }
    }
    if (rows.length === 0 && totalRows > 0) {
        return `empty_table_${totalRows}x${totalCells}`;
    }
    return (tableCond ? 'tblCond(' + tableCond + ')###' : '') + rows.join('###');
}

export function deduplicateTablesInHtml(html) {
    if (!html || typeof html !== 'string' || !html.includes('<table')) {
        return html;
    }

    // In browser environment, use DOMParser
    try {
        if (typeof DOMParser !== 'undefined') {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const tables = Array.from(doc.querySelectorAll('table'));
            if (tables.length <= 1) {
                return html;
            }

            let lastFp = null;
            let lastTable = null;
            let removedCount = 0;

            tables.forEach((table) => {
                const fp = extractTableFingerprint(table);
                // Only consider tables duplicate if they are immediately adjacent (no substantive content between them)
                let isAdjacent = false;
                if (lastTable) {
                    let sib = table.previousElementSibling;
                    while (sib && (sib.tagName === 'P' || sib.tagName === 'DIV') && (!sib.textContent.trim() || sib.innerHTML.trim() === '<br>')) {
                        sib = sib.previousElementSibling;
                    }
                    isAdjacent = (sib === lastTable);
                }

                if (isAdjacent && fp && lastFp === fp) {
                    const parent = table.parentElement;
                    table.remove();
                    if (parent && (parent.tagName === 'P' || parent.tagName === 'DIV') && !parent.textContent.trim() && parent.children.length === 0) {
                        parent.remove();
                    }
                    removedCount++;
                } else {
                    lastFp = fp;
                    lastTable = table;
                }
            });

            if (removedCount > 0) {
                console.log(`[Import] Found ${removedCount} adjacent duplicate table(s). Removed duplicate table(s).`);
                return doc.body.innerHTML;
            }
            return html;
        }
    } catch (e) {
        console.warn('DOMParser table deduplication failed, using fallback:', e);
    }

    // Fallback regex: only remove if consecutive/adjacent identical tables
    try {
        const tableRegex = /<table\b[^>]*>[\s\S]*?<\/table>/gi;
        const matches = [];
        let m;
        while ((m = tableRegex.exec(html)) !== null) {
            matches.push({ index: m.index, length: m[0].length, html: m[0], fp: extractTableFingerprint(m[0]) });
        }
        if (matches.length <= 1) return html;

        let result = html;
        for (let i = matches.length - 1; i >= 1; i--) {
            const curr = matches[i];
            const prev = matches[i - 1];
            const between = html.substring(prev.index + prev.length, curr.index);
            const isAdjacent = /^(?:\s*<p\b[^>]*>\s*(?:<br\s*\/?>)?\s*<\/p>|\s*)*$/i.test(between);
            if (isAdjacent && curr.fp && curr.fp === prev.fp) {
                result = result.substring(0, curr.index) + result.substring(curr.index + curr.length);
            }
        }
        return result;
    } catch (e) {
        console.warn('Regex table deduplication error:', e);
        return html;
    }
}

export function getProseMirrorTableFingerprint(tableNode) {
    const tableCond = (tableNode.attrs?.tableCondition || tableNode.attrs?.tableConditionData || '').trim().toLowerCase();
    const rows = [];
    let totalRows = 0;
    let totalCells = 0;
    tableNode.forEach((rowNode) => {
        if (rowNode.type.name === 'tableRow') {
            totalRows++;
            const rowCond = (rowNode.attrs?.rowCondition || '').trim().toLowerCase();
            const cells = [];
            rowNode.forEach((cellNode) => {
                if (cellNode.type.name === 'tableCell' || cellNode.type.name === 'tableHeader') {
                    totalCells++;
                    const vars = [];
                    const conds = [];
                    cellNode.descendants((innerNode) => {
                        if (innerNode.type.name === 'variableTag') {
                            const vName = innerNode.attrs?.label || innerNode.attrs?.key || innerNode.attrs?.variableKey;
                            if (vName) vars.push(String(vName).trim().toLowerCase());
                        }
                        if (innerNode.marks) {
                            innerNode.marks.forEach(m => {
                                if (m.type.name === 'conditionalBlock') {
                                    const rId = m.attrs?.ruleId || m.attrs?.rules;
                                    if (rId) conds.push(String(rId).trim().toLowerCase());
                                }
                            });
                        }
                    });
                    const text = (cellNode.textContent || '')
                        .replace(/[\u200B-\u200D\uFEFF]/g, '')
                        .replace(/\s+/g, ' ')
                        .trim()
                        .toLowerCase();
                    cells.push([text, vars.length ? 'var(' + vars.join(',') + ')' : '', conds.length ? 'cond(' + conds.join(',') + ')' : ''].filter(Boolean).join('::'));
                }
            });
            while (cells.length > 1 && cells[cells.length - 1] === '') {
                cells.pop();
            }
            if (cells.some(t => t.length > 0) || rowCond) {
                rows.push((rowCond ? 'rowCond(' + rowCond + ')::' : '') + cells.join(':::'));
            }
        }
    });
    if (rows.length === 0 && totalRows > 0) {
        return `empty_table_${totalRows}x${totalCells}`;
    }
    return (tableCond ? 'tblCond(' + tableCond + ')###' : '') + rows.join('###');
}

/**
 * Inspects the TipTap editor document state.
 * If immediately adjacent duplicate tables exist, removes 1 table directly in the editor.
 */
export function deduplicateTablesInEditor(editor) {
    if (!editor || !editor.state || !editor.state.doc) return false;
    const { doc } = editor.state;
    const tables = [];
    doc.descendants((node, pos) => {
        if (node.type.name === 'table') {
            tables.push({ node, pos });
            return false;
        }
    });

    if (tables.length <= 1) return false;

    let lastTable = null;
    let lastFp = null;
    const tablesToRemove = [];

    tables.forEach(({ node, pos }) => {
        const fp = getProseMirrorTableFingerprint(node);
        let isAdjacent = false;
        if (lastTable) {
            const betweenText = doc.textBetween(lastTable.pos + lastTable.node.nodeSize, pos, ' ').trim();
            isAdjacent = (betweenText === '');
        }

        if (isAdjacent && fp && lastFp === fp) {
            tablesToRemove.push({ node, pos });
        } else {
            lastTable = { node, pos };
            lastFp = fp;
        }
    });

    let tr = editor.state.tr;
    let modified = false;

    if (tablesToRemove.length > 0) {
        tablesToRemove.sort((a, b) => b.pos - a.pos);
        tablesToRemove.forEach(({ node, pos }) => {
            tr = tr.delete(pos, pos + node.nodeSize);
        });
        modified = true;
        console.log(`[Editor] Removed ${tablesToRemove.length} adjacent duplicate table(s) in editor.`);
    }

    // Check for duplicate paragraph lines immediately following any table in editor
    tables.forEach(({ node, pos }) => {
        const headerTexts = [];
        node.forEach((rowNode, rOffset, rIdx) => {
            if (rIdx === 0 && rowNode.type.name === 'tableRow') {
                rowNode.forEach(cell => {
                    const text = (cell.textContent || '').trim().toLowerCase();
                    if (text) headerTexts.push(text);
                });
            }
        });

        if (headerTexts.length >= 2) {
            const tableEndPos = pos + node.nodeSize;
            let currentPos = tableEndPos;
            const duplicatePositions = [];

            while (currentPos < doc.content.size) {
                const afterNode = doc.nodeAt(currentPos);
                if (!afterNode || afterNode.type.name !== 'paragraph') break;

                let hasSpecialContent = false;
                afterNode.descendants(child => {
                    if (child.type.name === 'variableTag' || child.type.name === 'whiteBlankImage') hasSpecialContent = true;
                    if (child.marks && child.marks.some(m => m.type.name === 'conditionalBlock')) hasSpecialContent = true;
                });
                const text = (afterNode.textContent || '').trim().toLowerCase();
                if (hasSpecialContent || text.startsWith('#') || text.startsWith('{') || text.startsWith('|')) {
                    break;
                }
                const isHdr = headerTexts.includes(text);
                const isNum = /^\d+$/.test(text);
                const isVar = text.startsWith('<<') || text.startsWith('◇') || text.includes('loan') || text.includes('interest') || text.includes('amount') || text.includes('tenure');
                const isBlank = !text;

                if (duplicatePositions.length === 0) {
                    if (isHdr && text === headerTexts[0]) {
                        duplicatePositions.push({ pos: currentPos, size: afterNode.nodeSize });
                        currentPos += afterNode.nodeSize;
                        continue;
                    } else {
                        break;
                    }
                } else {
                    if (isHdr || isNum || isVar || isBlank || (text.length < 50 && !text.includes('.') && !text.includes('agreement') && !text.includes('claimant') && !text.includes('respondent'))) {
                        duplicatePositions.push({ pos: currentPos, size: afterNode.nodeSize });
                        currentPos += afterNode.nodeSize;
                    } else {
                        break;
                    }
                }
            }

            if (duplicatePositions.length >= headerTexts.length) {
                duplicatePositions.sort((a, b) => b.pos - a.pos);
                duplicatePositions.forEach(({ pos: p, size }) => {
                    tr = tr.delete(p, p + size);
                });
                modified = true;
                console.log(`[Editor] Removed ${duplicatePositions.length} duplicate table content line(s) below table.`);
            }
        }
    });

    if (modified) {
        editor.view.dispatch(tr);
        return true;
    }
    return false;
}
